import { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useLocation, useNavigate } from 'react-router';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  createShopifyIntegrationForTcr,
  createTcrFromShopifyAppInstall,
  getTenantCreationRequest,
} from '@/lib/api-client';

export const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL;

export function ShopifyAppPage() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const lastIntegrationKeyRef = useRef<string | null>(null);

  const state = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return urlParams.get('state');
  }, [search]);

  const session = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return urlParams.get('session');
  }, [search]);

  const hmac = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return urlParams.get('hmac');
  }, [search]);

  const code = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return urlParams.get('code');
  }, [search]);

  const host = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return urlParams.get('host');
  }, [search]);

  const shop = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return urlParams.get('shop');
  }, [search]);

  const createIntegrationForTcr = useCallback(
    async (data: {
      slug: string;
      code: string;
      hmac: string;
      host: string;
      shop: string;
    }) => {
      try {
        const response = await createShopifyIntegrationForTcr(data);
        console.log('Shopify integration created successfully', response);
        navigate(`/signup/${data.slug}/select-plan`);
      } catch (error) {
        console.error('Failed to create Shopify integration for TCR', error);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'We could not complete your Shopify setup. Please try again from Shopify.'
        );
        setStatus('error');
      }
    },
    [navigate]
  );

  const createTcr = useCallback(
    async (data: {
      code: string;
      hmac: string;
      host: string;
      shop: string;
    }) => {
      try {
        const response = await createTcrFromShopifyAppInstall(data);
        if (response.magicLink) {
          window.location.href = response.magicLink;
        } else {
          setErrorMessage(
            'We could not retrieve your activation link. Please reopen the Korza app from Shopify.'
          );
          setStatus('error');
        }
      } catch (error) {
        console.error('Failed to create TCR from Shopify app install', error);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'We could not complete your Shopify setup. Please try again from Shopify.'
        );
        setStatus('error');
      }
    },
    [setErrorMessage, setStatus]
  );

  useEffect(() => {
    const handleShopifyRedirect = async () => {
      if (session) {
        navigate('/', { replace: true });
        return;
      }

      if (!hmac || !host || !shop) {
        navigate('/', { replace: true });
        return;
      }

      setStatus('loading');
      setErrorMessage(null);

      if (code) {
        const integrationKey = [state, code, hmac, host, shop]
          .map((value) => value ?? 'unknown')
          .join('|');

        if (integrationKey === lastIntegrationKeyRef.current) {
          return;
        }

        lastIntegrationKeyRef.current = integrationKey;

        if (!state || state === 'undefined') {
          await createTcr({ code, hmac, host, shop });
          return;
        }

        const tcr = await getTenantCreationRequest(state);

        if (tcr !== null && tcr.isShopifyIntegrationConfigured) {
          navigate('/', { replace: true });
          return;
        }

        await createIntegrationForTcr({
          slug: state,
          code,
          hmac,
          host,
          shop,
        });
        return;
      }

      const redirectUrl = `${window.location.origin}/shopify-app`;
      window.location.href = `${API_BASE_URL}/shopify-app/activate?storeWebsiteDomain=${shop}&redirectUrl=${encodeURIComponent(
        redirectUrl
      )}`;
    };

    handleShopifyRedirect();
  }, [
    code,
    createIntegrationForTcr,
    createTcr,
    hmac,
    host,
    navigate,
    session,
    shop,
    state,
  ]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
      {status === 'error' ? (
        <Alert variant="destructive" className="max-w-md text-left">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle>We hit a snag</AlertTitle>
          <AlertDescription>
            {errorMessage ??
              'Something unexpected happened while redirecting. Please try reopening Korza from Shopify.'}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-base font-medium">
            Finishing your Shopify setup...
          </p>
          <p className="text-sm text-muted-foreground">
            This page will redirect you automatically. You can close it if
            nothing happens after a few seconds.
          </p>
        </div>
      )}
    </div>
  );
}
